import { useContext } from 'react';
import styled, { css } from 'styled-components';

import { Icon } from '@creditornot/alchemy-react';
import * as icons from '@creditornot/alchemy-react/icons';
import { typographyClassName, typographyCss } from '@creditornot/cb-ingredients';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { Tooltip } from '@creditornot/cb-popover';

import { DataTableContext } from './DataTableContext';
import { RowDragSelect } from './RowDragSelect';
import { AlignHorizontal, AlignVertical, ColumnType, SelectMode } from './types';
import { horizontalContentAlignmentMap, verticalContentAlignmentMap } from './utils';

const HeaderRow = styled.div<{ $isStickyHeader: boolean }>`
  color: ${color.textSubdued};
  z-index: 2;

  @supports (grid-template-rows: subgrid) {
    grid-template-columns: subgrid;
    grid-column: 1 / -1;
    display: grid;
    grid-auto-flow: row;

    ${({ $isStickyHeader }) =>
      $isStickyHeader &&
      css`
        background: ${color.bg};
        position: sticky;
        top: 0;
      `}
  }

  @supports not (grid-template-rows: subgrid) {
    overflow-x: auto;
    display: contents;

    ${({ $isStickyHeader }) =>
      $isStickyHeader &&
      css`
        ${Column} , .header-select {
          background: ${color.bg};
          position: sticky;
          top: 0;
        }
      `}
  }

  .header-select {
    border-bottom: 1px solid ${color.border};
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  background: ${color.bg};
  border-bottom: 1px solid ${color.border};
  padding-top: 12px;
  padding-bottom: 12px;

  &:first-child {
    padding-left: 24px;
  }
`;

const HeaderColumn = styled(Column)<{
  $align?: AlignHorizontal;
  $alignVertical: AlignVertical;
}>`
  padding-left: 8px;
  padding-right: 8px;
  justify-content: ${({ $alignVertical }) => verticalContentAlignmentMap[$alignVertical || 'top']};
  align-items: ${({ $align }) => horizontalContentAlignmentMap[$align || 'left']};
`;

const HeaderColumnName = styled.div<{ $align: AlignHorizontal }>`
  ${typographyCss.caption}

  color: ${color.textSubdued};
  font-size: 14px;
  text-align: ${({ $align }) => $align};
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const MenuWithExpanderColumn = styled(Column)``;

type Props<Row> = {
  columns: ColumnType<Row>[];
  isStickyHeader: boolean;
  hasMenuExpandColumn?: boolean;
  hasDragSelectColumn: boolean;
  handleToggleAllRows: (value: SelectMode) => void;
  isSelectDisabled: boolean;
};

export const DataTableHeader = <Row extends Record<string, unknown>>({
  columns,
  isStickyHeader,
  hasMenuExpandColumn = false,
  hasDragSelectColumn,
  handleToggleAllRows,
  isSelectDisabled,
}: Props<Row>) => {
  const { selectionState } = useContext(DataTableContext);

  const getSelectionMode = () => {
    const values = Object.values(selectionState);

    if (values.length === 0) {
      return 'NONE';
    }

    const hasAll = values.every((value) => value === 'ALL');
    const hasSome = values.some((value) => value === 'ALL' || value === 'SOME');

    if (hasAll) {
      return 'ALL';
    }

    return hasSome ? 'SOME' : 'NONE';
  };

  return (
    <HeaderRow $isStickyHeader={isStickyHeader} role="row" className={typographyClassName.footnote}>
      <RowDragSelect
        handleToggleRow={handleToggleAllRows}
        selectionMode={getSelectionMode()}
        hasDragSelectColumn={hasDragSelectColumn}
        hideSelection={isSelectDisabled}
        className="header-select"
        name="select-all-rows"
        role="columnheader"
      />

      {columns.map((column) => {
        const {
          alignName = 'left',
          alignNameVertical = 'top',
          header,
          className,
          info,
          id,
        } = column;
        return (
          <HeaderColumn
            className={className}
            key={id}
            $align={alignName}
            $alignVertical={alignNameVertical}
            role="columnheader"
          >
            {!!header && (
              <HeaderColumnName $align={alignName}>
                <span>{header}</span>
                {!!info && (
                  <span>
                    <Tooltip content={info}>
                      <Icon
                        source={icons.infoCircleFilled}
                        size="tiny"
                        style={{ marginLeft: '4px' }}
                      />
                    </Tooltip>
                  </span>
                )}
              </HeaderColumnName>
            )}
          </HeaderColumn>
        );
      })}
      {hasMenuExpandColumn && <MenuWithExpanderColumn role="cell" />}
    </HeaderRow>
  );
};
